import { zodResolver } from '@hookform/resolvers/zod'
import { useState, type FormEvent } from 'react'
import { useForm } from 'react-hook-form'

import type { SelfAssessmentDefinition } from '@/src/domains/self-assessments/schemas'

import { SelfAssessmentStep } from './SelfAssessmentWizard.constants'
import type { SelfAssessmentWizardContextValue } from './SelfAssessmentWizard.context'
import { SelfAssessmentWizardSchema } from './SelfAssessmentWizard.schema'

const getStep = (position: number, questionCount: number) => {
  if (position < 0) {
    return SelfAssessmentStep.INTRO
  }

  return position === questionCount ? SelfAssessmentStep.RESULT : SelfAssessmentStep.QUESTION
}

export const useSelfAssessmentWizardForm = (definition: SelfAssessmentDefinition) => {
  const questionCount = definition.questions.length

  const form = useForm({
    resolver: zodResolver(SelfAssessmentWizardSchema),
    defaultValues: { answers: definition.questions.map(() => null) },
  })

  const [position, setPosition] = useState(-1)
  const [hasNavigated, setHasNavigated] = useState(false)

  const isLastQuestion = position === questionCount - 1

  const handleStart = () => {
    setHasNavigated(true)
    setPosition(0)
  }

  const handleReset = () => {
    form.reset()
    setPosition(-1)
  }

  const handleBack = () => {
    if (position === 0) {
      handleReset()
    } else {
      setPosition(position - 1)
    }
  }

  const handleNextQuestion = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (await form.trigger(`answers.${position}`)) {
      setPosition(position + 1)
    }
  }

  const handleShowResult = form.handleSubmit(() => setPosition(questionCount))

  const wizard: SelfAssessmentWizardContextValue = {
    definition,
    step: getStep(position, questionCount),
    questionIndex: position,
    isLastQuestion,
    hasNavigated,
    handleStart,
    handleBack,
  }

  return {
    form,
    wizard,
    handleSubmit: isLastQuestion ? handleShowResult : handleNextQuestion,
    handleReset,
  }
}
